import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { Autocomplete, DialogContent, MenuItem, Select, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getEmAssignment } from 'redux/reducers/settings/selectors';
import moment from 'moment-timezone';
import { updateSettingsRequest } from 'redux/reducers/settings/reducer';
import { nanoid } from '@reduxjs/toolkit';
import { TEditSettingProps } from './types';
import {
	EDIT_SETTING_TEXT,
	IOSS_SALE_OVER_THRESHOLD_OPTIONS,
	PRODUCT_LANGUAGE_OPTIONS,
} from '../constants';

export const EditSettingDialog: FC<TEditSettingProps> = ({ setting, setClose }) => {
	const dispatch = useDispatch();
	const emAssignment = useSelector(getEmAssignment);

	const [timezone, setTimezone] = useState<string | null>(null);
	const [language, setLanguage] = useState<string[] | null>(null);
	const [iossSaleOverThreshold, setIossSaleOverThreshold] = useState<string>('');

	useEffect(() => {
		if (!emAssignment || !setting) return;
		setTimezone(emAssignment.timezone ?? null);
		setLanguage(
			PRODUCT_LANGUAGE_OPTIONS.find((item) => item[0] === emAssignment.productLanguage) ?? null,
		);
		setIossSaleOverThreshold(emAssignment.iossSaleOverThreshold?.toUpperCase() ?? '');
	}, [emAssignment, setting]);

	const handleEdit = () => {
		if (!emAssignment) return;
		dispatch(
			updateSettingsRequest({
				timezone: timezone || emAssignment.timezone,
				productLanguage: language?.[0] || emAssignment.productLanguage,
				iossSaleOverThreshold: iossSaleOverThreshold || emAssignment.iossSaleOverThreshold,
			}),
		);
		setClose();
	};

	return (
		<Dialog
			open={!!setting}
			onClose={setClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">{setting ? EDIT_SETTING_TEXT[setting] : ''}</DialogTitle>
			<DialogContent>
				{setting === 'timezone' && (
					<Autocomplete
						sx={{
							'& .MuiInputBase-root': {
								borderRadius: '12px!important',
							},
						}}
						value={timezone}
						onChange={(event: any, newValue: string | null) => {
							setTimezone(newValue);
						}}
						options={moment.tz.names()}
						renderInput={(params) => <TextField {...params} />}
						renderOption={(props, option) => (
							<MenuItem
								{...props}
								key={nanoid()}
								sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
							>
								<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
									<div>{option}</div>
									<div style={{ color: 'gray' }}>{moment().tz(option).format('Z')}</div>
								</div>
							</MenuItem>
						)}
					/>
				)}
				{setting === 'productLanguage' && (
					<Autocomplete<string[]>
						sx={{
							'& .MuiInputBase-root': {
								borderRadius: '12px!important',
							},
						}}
						value={language}
						onChange={(event: any, newValue: string[] | null) => {
							setLanguage(newValue);
						}}
						options={PRODUCT_LANGUAGE_OPTIONS}
						getOptionLabel={(option) => option[1]}
						isOptionEqualToValue={(option, value) => option[0] === value[0]}
						renderInput={(params) => <TextField {...params} />}
						renderOption={(props, option) => (
							<MenuItem
								{...props}
								key={option[0]}
								sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
							>
								<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
									<div>{option[1]}</div>
								</div>
							</MenuItem>
						)}
					/>
				)}

				{setting === 'iossSaleOverThreshold' && (
					<Select
						sx={{
							borderRadius: '12px!important',
						}}
						value={iossSaleOverThreshold}
						fullWidth
						onChange={(event) => {
							setIossSaleOverThreshold(event.target.value);
						}}
					>
						{IOSS_SALE_OVER_THRESHOLD_OPTIONS.map((item) => (
							<MenuItem key={item[0]} value={item[0]}>
								{item[1]}
							</MenuItem>
						))}
					</Select>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={setClose}>Cancel</Button>
				<Button onClick={handleEdit} autoFocus>
					Edit
				</Button>
			</DialogActions>
		</Dialog>
	);
};
